import { inject, Injectable } from '@angular/core';
import { Observable, from, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { GoodsSkuControllerService } from '@app/api/api/goodsSkuController.service';
import { BlobConverterUtil } from '@shared/utils/blob-converter.util';
import {
  mapPageContentToPaginatedPage,
  PaginatedDropdownLoadParams,
  PaginatedOptionsPage,
} from '@shared/utils/paginated-dropdown.util';

export interface SkuOptionsQuery {
  /** SKU status list (default: [3] effective) */
  status?: number[];
  valueKey?: 'code' | 'id';
  /** Field used when filtering the SKU dropdown (default: both fields). */
  searchBy?: 'skuCode' | 'skuName' | 'both';
}

@Injectable({ providedIn: 'root' })
export class SkuOptionsService {
  private readonly goodsSkuController = inject(GoodsSkuControllerService);

  getOptionsPage(
    params: PaginatedDropdownLoadParams,
    query: SkuOptionsQuery = {},
  ): Observable<PaginatedOptionsPage<string | number>> {
    const searchText = params.searchText?.trim();
    const valueKey = query.valueKey ?? 'code';
    const searchBy = query.searchBy ?? 'both';
    const skuName =
      searchBy === 'skuCode' ? undefined : searchText || undefined;
    const skuCode =
      searchBy === 'skuName' ? undefined : searchText || undefined;

    return this.goodsSkuController
      .getList3(
        undefined,
        undefined,
        undefined,
        0,
        undefined,
        query.status ?? [3],
        skuName,
        skuCode,
        params.page - 1,
        params.size,
        undefined,
        undefined,
      )
      .pipe(
        switchMap((response: unknown) => {
          if (BlobConverterUtil.isBlob(response)) {
            return from(BlobConverterUtil.convertToJson(response));
          }
          return of(response);
        }),
        map((response: Record<string, unknown>) =>
          mapPageContentToPaginatedPage(
            {
              pageContent: BlobConverterUtil.extractPageContent(response, []),
              totalElements: response['totalElements'] as number | undefined,
              hasNextPage: response['hasNextPage'] as boolean | undefined,
            },
            (item: Record<string, unknown>) => {
              const code = String(item['skuCode'] ?? '');
              const name = String(item['skuName'] ?? '');
              return {
                label: name ? `${code} - ${name}` : code,
                value:
                  valueKey === 'id'
                    ? Number(item['id'] ?? 0)
                    : code,
              };
            },
          ),
        ),
        catchError(() => of({ options: [], pageContentLength: 0 })),
      );
  }
}
